/**
 * Hawaii Tee Times - Booking Systems Validation
 * CRITICAL: Validates all 3 booking systems work correctly
 */

class BookingValidator {
  constructor() {
    this.systems = {
      ezlinks: false,
      realTimeAPI: false,
      bookThatApp: false
    };
    this.init();
  }

  init() {
    console.log('🏌️ Initializing Hawaii Tee Times Booking Validation...');

    // Wait for DOM and all scripts to load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.validateSystems());
    } else {
      this.validateSystems();
    }
  }

  validateSystems() {
    // Check if this is an FT product
    const isFTProduct = this.isFTProduct();

    if (isFTProduct) {
      console.log('🔍 FT Product detected - only validating BookThatApp');
      this.validateBookThatApp();
    } else {
      this.validateEzLinks();
      this.validateRealTimeAPI();
      this.validateBookThatApp();
    }
    this.reportValidation();
  }

  isFTProduct() {
    // Check if this is an FT product by title or tags
    if (window.productTitle) {
      if (window.productTitle.includes('FT') || window.productTitle.includes('ft')) {
        return true;
      }
    }
    if (window.productTags) {
      return window.productTags?.some(tag =>
        tag === 'FT' || tag === 'ForeTees' || tag === 'foretees'
      );
    }
    return false;
  }

  validateEzLinks() {
    console.log('🔍 Validating EzLinks System...');

    // Check for EzLinks variants and metafields
    if (window.ezlinksVariants && window.ezlinksMetafields) {
      console.log('✅ EzLinks variants detected:', window.ezlinksVariants.length);
      console.log('✅ EzLinks metafields loaded');
      this.systems.ezlinks = true;

      // Check for dynamic pricing elements
      const dynamicPricing = document.querySelector('.variant-pricing-cards');
      if (dynamicPricing) {
        console.log('✅ EzLinks dynamic pricing UI found');
      }

      // Check for tee-times snippet
      const teeTimesContainer = document.getElementById('ezlinks-times-container');
      if (teeTimesContainer) {
        console.log('✅ EzLinks tee-times container found');
      }
    } else {
      console.warn('⚠️ EzLinks data not found - may not be EzLinks product');
    }
  }

  validateRealTimeAPI() {
    console.log('🔍 Validating Real-Time API System...');

    // Check for courseid tag
    const courseIdTag = window.productTags?.find(tag => tag.startsWith('courseid:'));
    if (courseIdTag) {
      const courseId = courseIdTag.slice(9);
      console.log('✅ Course ID found:', courseId);

      // Check for API class
      if (window.TeeTimesAPI) {
        console.log('✅ TeeTimesAPI class loaded');
        this.systems.realTimeAPI = true;

        // Test API connection with Shopify product ID (like old theme)
        console.log('🔍 Debug - window.productId:', window.productId);
        console.log('🔍 Debug - courseId from tag:', courseId);
        this.testAPIConnection(window.productId);
      } else {
        console.error('❌ TeeTimesAPI class not found');
      }
    } else {
      console.warn('⚠️ No courseid tag found - Real-Time API not applicable');
    }
  }

  validateBookThatApp() {
    console.log('🔍 Validating BookThatApp System...');

    // Check for BTA config
    if (window.BtaConfig || window.btaConfig) {
      console.log('✅ BookThatApp config found');
      this.systems.bookThatApp = true;

      // Check for BTA widgets script
      if (window.BTA || document.querySelector('script[src*="bta-widgets"]')) {
        console.log('✅ BookThatApp widgets loaded');
      }

      // Check for BTA metafields
      if (window.productMetafields?.bookthatapp) {
        console.log('✅ BookThatApp metafields detected');
      }
    } else {
      console.warn('⚠️ BookThatApp config not found');
    }
  }

  async testAPIConnection(productId) {
    try {
      // Use current date + 1 hour like the old theme
      const now = new Date();
      const testDate = now.getTime() + 3600000; // Add 1 hour

      const response = await fetch(`https://hawaii-tee-times-dev.replit.app/api/courses/${productId}/times?date=${testDate}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        console.log('✅ Real-Time API connection successful');
      } else {
        console.warn('⚠️ Real-Time API connection failed:', response.status);
      }
    } catch (error) {
      console.warn('⚠️ Real-Time API test failed:', error.message);
    }
  }

  reportValidation() {
    const isFTProduct = this.isFTProduct();

    console.log('\n📊 BOOKING SYSTEMS VALIDATION REPORT:');
    console.log('=====================================');

    if (isFTProduct) {
      console.log('🏷️ FT (ForeTees) Product Detected');
      console.log('Note: FT products use BookThatApp exclusively\n');

      const icon = this.systems.bookThatApp ? '✅' : '❌';
      console.log(`${icon} BookThatApp: ${this.systems.bookThatApp ? 'ACTIVE' : 'INACTIVE'}`);

      if (this.systems.bookThatApp) {
        console.log('\n✨ PERFECT: BookThatApp is active for FT product');
      } else {
        console.error('\n🚨 CRITICAL: BookThatApp not detected for FT product!');
        this.showErrorMessage();
      }
    } else {
      const totalSystems = Object.keys(this.systems).length;
      const activeSystems = Object.values(this.systems).filter(Boolean).length;

      Object.entries(this.systems).forEach(([system, status]) => {
        const icon = status ? '✅' : '❌';
        const name = system.charAt(0).toUpperCase() + system.slice(1);
        console.log(`${icon} ${name}: ${status ? 'ACTIVE' : 'INACTIVE'}`);
      });

      console.log(`\n🎯 Active Systems: ${activeSystems}/${totalSystems}`);

      if (activeSystems === 0) {
        console.error('🚨 CRITICAL: No booking systems detected!');
        this.showErrorMessage();
      } else if (activeSystems >= 2) {
        console.log('✨ EXCELLENT: Multiple booking systems active');
      } else {
        console.log('✅ GOOD: At least one booking system active');
      }
    }

    // Store validation results globally
    window.bookingValidation = this.systems;
  }

  showErrorMessage() {
    const bookingWidget = document.querySelector('.booking-widget');
    if (bookingWidget) {
      bookingWidget.innerHTML = `
        <div class="booking-error">
          <h3>⚠️ Booking System Error</h3>
          <p>No booking systems detected. Please check:</p>
          <ul>
            <li>Product has courseid tag for Real-Time API</li>
            <li>BookThatApp metafields configured</li>
            <li>EzLinks variants setup correctly</li>
          </ul>
        </div>
      `;
    }
  }
}

// Auto-initialize on product pages
if (window.location.pathname.includes('/products/')) {
  new BookingValidator();
}

// Manual validation function for testing
window.validateBookingSystems = () => new BookingValidator();